<template>
    <div class="fingering-view">
        <div class="header">
            <h1 class="text-header text-primary">Flute Fingering Chart</h1>
            <p class="subtitle text-medium-emphasis">
                Standard fingerings for all notes in the flute's range
            </p>
        </div>

        <div class="registers">
            <section class="register bg-surface-light">
                <h2 class="text-secondary">Low Register (C4 - B4)</h2>
                <div :class="['notes-container', layout]">
                    <NoteWithFingering
                        v-for="note in lowRegister"
                        :key="note"
                        :note="note"
                        :duration="duration"
                        :showNoteLabel="showNoteLabels"
                        :noteWidth="noteSize.width"
                        :noteHeight="noteSize.height"
                        :fingeringWidth="fingeringSize.width"
                        :fingeringHeight="fingeringSize.height"
                    />
                </div>
            </section>

            <section class="register bg-surface-light">
                <h2 class="text-secondary">Middle Register (C5 - B5)</h2>
                <div :class="['notes-container', layout]">
                    <NoteWithFingering
                        v-for="note in middleRegister"
                        :key="note"
                        :note="note"
                        :duration="duration"
                        :showNoteLabel="showNoteLabels"
                        :noteWidth="noteSize.width"
                        :noteHeight="noteSize.height"
                        :fingeringWidth="fingeringSize.width"
                        :fingeringHeight="fingeringSize.height"
                    />
                </div>
            </section>

            <section class="register bg-surface-light">
                <h2 class="text-secondary">High Register (C6 - C7)</h2>
                <div :class="['notes-container', layout]">
                    <NoteWithFingering
                        v-for="note in highRegister"
                        :key="note"
                        :note="note"
                        :duration="duration"
                        :showNoteLabel="showNoteLabels"
                        :noteWidth="noteSize.width"
                        :noteHeight="noteSize.height"
                        :fingeringWidth="fingeringSize.width"
                        :fingeringHeight="fingeringSize.height"
                    />
                </div>
            </section>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import NoteWithFingering from "../components/NoteWithFingering.vue";
import { standardFluteFingerings } from "../data/fluteFingeringChart";

const layout = "grid";
const duration = "quarter";
const showNoteLabels = true;

// Get all notes from the fingering chart in chromatic order
const chromaticScale = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
];

// Generate ordered list of notes for each register
const lowRegister = computed(() => {
    return chromaticScale
        .map((note) => `${note}4`)
        .filter((note) => note in standardFluteFingerings);
});

const middleRegister = computed(() => {
    return chromaticScale
        .map((note) => `${note}5`)
        .filter((note) => note in standardFluteFingerings);
});

const highRegister = computed(() => {
    const notes: string[] = [];
    // C6 through B6
    chromaticScale.forEach((note) => {
        const noteName = `${note}6`;
        if (noteName in standardFluteFingerings) {
            notes.push(noteName);
        }
    });
    // C7
    if ("C7" in standardFluteFingerings) {
        notes.push("C7");
    }
    return notes;
});

const noteSize = computed(() => ({
    width: layout === "grid" ? 180 : 200,
    height: layout === "grid" ? 120 : 120,
}));

const fingeringSize = computed(() => ({
    width: layout === "grid" ? 200 : 500,
    height: layout === "grid" ? 120 : 180,
}));
</script>

<style scoped>
.fingering-view {
    max-width: 1600px;
    margin: 0 auto;
    padding: 20px;
}

.header {
    text-align: center;
    margin-bottom: 30px;
}

.header h1 {
    font-size: 2.5em;
    margin-bottom: 10px;
}

.subtitle {
    font-size: 1.1em;
    margin-bottom: 40px;
    opacity: 0.7;
}

.registers {
    display: flex;
    flex-direction: column;
    gap: 40px;
}

.register {
    padding: 20px;
    border-radius: 8px;
}

.register h2 {
    font-size: 1.5em;
    margin-bottom: 20px;
    padding-bottom: 10px;
}

.notes-container.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 12px;
    justify-items: center;
}

.notes-container.list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
}

@media (max-width: 900px) {
    .notes-container.grid {
        grid-template-columns: 1fr;
    }
}
</style>
